import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { AdminControlPanel } from "@/components/Admin/AdminControlPanel";
import { Button } from "@/components/Button/Button";
import { IconButton } from "@/components/Button/IconButton";
import { SwitchToggle } from "@/components/SwitchToggle/SwitchToggle";
import { AppText } from "@/components/Text/AppText";
import { InputLabel } from "@/components/form/InputLabel";

interface DashboardAdmin {
    onFetchTemplates: () => Promise<void>;
    onShowTemplates: () => void;
    showTemplates: boolean;
    hasTemplates: boolean;
}

export const DashboardAdmin = ({
    onFetchTemplates,
    onShowTemplates,
    showTemplates,
    hasTemplates,
}: DashboardAdmin) => {
    const [tenantOverride, setTenantOverride] = useState<string | null>(
        window.localStorage.getItem("tenantOverride") || null,
    );

    return (
        <AdminControlPanel topPosition={80}>
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 border-b pb-2">
                    <InputLabel label="Tenant Override" />
                    <div className="flex w-full flex-row gap-2">
                        <input
                            className="border-0.5 w-full rounded-lg text-xs"
                            value={tenantOverride || ""}
                            onChange={e => setTenantOverride(e.target.value)}
                            placeholder="Tenant subdomain (e.g. app)"
                        />
                        <IconButton
                            color="grey"
                            onClick={() => {
                                window.location.reload();
                            }}
                        >
                            <RefreshCw size={16} />
                        </IconButton>
                    </div>
                    <div className="flex flex-row gap-2">
                        <Button
                            size="small"
                            onClick={() => {
                                setTenantOverride("");
                                window.localStorage.removeItem(
                                    "tenantOverride",
                                );
                            }}
                        >
                            Clear
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setTenantOverride(tenantOverride || "");
                                window.localStorage.setItem(
                                    "tenantOverride",
                                    tenantOverride || "",
                                );
                            }}
                        >
                            Set Tenant
                        </Button>
                    </div>
                    <AppText
                        variant="footnote"
                        className="!font-bold text-error-500"
                    >
                        Reload required to apply changes
                    </AppText>
                </div>
                <div className="mb-2 flex flex-col gap-1 text-xs">
                    <AppText
                        variant="footnote"
                        className="!font-semibold text-primary-500"
                    >
                        Public
                    </AppText>
                    <AppText variant="footnote">
                        Visible to all, listed on examples page
                    </AppText>
                    <AppText
                        variant="footnote"
                        className="!font-semibold text-error-500"
                    >
                        Private
                    </AppText>
                    <AppText variant="footnote">
                        Visible to admins, listed on examples page
                    </AppText>
                    <AppText
                        variant="footnote"
                        className="!font-semibold text-neutral-500"
                    >
                        Unlisted
                    </AppText>
                    <AppText variant="footnote">
                        Visible to admins, not on examples page
                    </AppText>
                </div>
                <div className="mb-2 flex items-center gap-2">
                    <AppText variant="labelsbuttons">Show Templates?</AppText>
                    <SwitchToggle
                        enabled={showTemplates}
                        disabled={!hasTemplates}
                        onToggle={onShowTemplates}
                    />
                </div>
                <Button onClick={onFetchTemplates}>Fetch</Button>
            </div>
        </AdminControlPanel>
    );
};
